import React, { useState, useEffect, useCallback } from 'react';
import { RNLLoader, useToast } from '../UI';
import * as api from '../../api';

const STATUS_CFG = {
  draft:                  { label: 'Draft',                color: '#64748b' },
  submitted:              { label: 'Submitted',            color: '#2563eb' },
  awaiting_cc_review:     { label: 'Awaiting CC Review',   color: '#d97706' },
  under_cc_review:        { label: 'Under CC Review',      color: '#d97706' },
  partially_reviewed:     { label: 'Partially Reviewed',   color: '#d97706' },
  cc_reviewed:            { label: 'CC Reviewed',          color: '#7c3aed' },
  awaiting_store_review:  { label: 'Awaiting Store Review',color: '#7c3aed' },
  store_reviewed:         { label: 'Store Reviewed',       color: '#0891b2' },
  pending_approval:       { label: 'Pending Approval',     color: '#0891b2' },
  partially_approved:     { label: 'Partially Approved',   color: '#0891b2' },
  approved:               { label: 'Approved',             color: '#16a34a' },
  rejected:               { label: 'Rejected',             color: '#ef4444' },
  on_hold:                { label: 'On Hold',              color: '#64748b' },
  returned:               { label: 'Returned',             color: '#f97316' },
};

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-GB'); } catch { return v; }
};
const fmtDateTime = (v) => {
  if (!v) return '';
  try { return new Date(v).toLocaleString(); } catch { return v; }
};

const fieldLabel = {
  fontWeight: 700, color: '#64748b', fontSize: 10,
  textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 3,
};
const fieldValue = { fontWeight: 600, color: '#1e3a8a', fontSize: 13 };

// ─── Nested line/material display for details view ────────────────────────────

function LineDetailRow({ li, index }) {
  const [open, setOpen] = React.useState(false);
  const firstSub = li.sub_items?.[0];
  const summary = firstSub?.material_description
    ? firstSub.material_description + (li.sub_items.length > 1 ? ` +${li.sub_items.length - 1} more` : '')
    : `Line ${index + 1}`;

  return (
    <>
      <tr
        onClick={() => setOpen(o => !o)}
        style={{ background: open ? '#eff6ff' : '#fff', borderBottom: open ? 'none' : '1px solid #e2e8f0', cursor: 'pointer' }}
      >
        <td style={{ padding: '7px 10px', fontWeight: 700, color: '#1e3a8a', userSelect: 'none', whiteSpace: 'nowrap', fontSize: 12 }}>
          <span style={{ marginRight: 5, fontSize: 10, color: '#64748b' }}>{open ? '▼' : '▶'}</span>
          {index + 1}
        </td>
        <td style={{ padding: '7px 10px', fontWeight: 600, fontSize: 12 }}>{summary}</td>
        <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 12 }}>{li.required_quantity ?? '—'}</td>
        <td style={{ padding: '7px 10px', textAlign: 'center', fontSize: 12, color: '#475569' }}>{li.unit_of_measure || '—'}</td>
        <td style={{ padding: '7px 10px', textAlign: 'right', fontSize: 12 }}>
          {li.estimated_unit_rate != null ? `₦${Number(li.estimated_unit_rate).toLocaleString()}` : '—'}
        </td>
        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 700, fontSize: 12, color: '#1e3a8a' }}>
          {li.estimated_total != null ? `₦${Number(li.estimated_total).toLocaleString()}` : '—'}
        </td>
        <td style={{ padding: '7px 10px', fontSize: 11, color: '#64748b' }}>{li.remarks || '—'}</td>
        <td style={{ padding: '7px 10px', textAlign: 'center' }}>
          <span style={{ background: '#fce7f3', color: '#be185d', padding: '2px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
            {li.sub_items?.length ?? 0} mat.
          </span>
        </td>
      </tr>
      {open && li.sub_items?.length > 0 && (
        <tr style={{ borderBottom: '1.5px solid #c7d2fe' }}>
          <td colSpan={8} style={{ padding: '0 0 8px 28px', background: '#f8faff' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: '#fff0f4' }}>
                  {['Material', 'Description', 'Specification', 'Brand', 'Req Qty', 'UOM', 'Est. Rate', 'Est. Cost'].map(h => (
                    <th key={h} style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 700, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#be185d', borderBottom: '1px solid #fce7f3', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {li.sub_items.map((sub, si) => (
                  <tr key={sub.sub_item_id || si} style={{ background: si % 2 === 0 ? '#fefcfd' : '#fff8fa', borderBottom: '1px solid #fce7f3' }}>
                    <td style={{ padding: '5px 8px', color: '#7c3aed', fontWeight: 600, fontSize: 11 }}>{sub.material_code || (sub.material_id ? sub.material_id.slice(0, 8) + '…' : '—')}</td>
                    <td style={{ padding: '5px 8px', fontWeight: 600, minWidth: 160 }}>{sub.material_description || '—'}</td>
                    <td style={{ padding: '5px 8px', color: '#475569' }}>{sub.specification || '—'}</td>
                    <td style={{ padding: '5px 8px', color: '#475569' }}>{sub.brand_model || '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{sub.required_quantity ?? '—'}</td>
                    <td style={{ padding: '5px 8px', color: '#475569' }}>{sub.unit_of_measure || '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right' }}>{sub.estimated_unit_rate != null ? `₦${Number(sub.estimated_unit_rate).toLocaleString()}` : '—'}</td>
                    <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: '#1e3a8a' }}>{sub.estimated_total != null ? `₦${Number(sub.estimated_total).toLocaleString()}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function CategoryDetailBlock({ cat, index }) {
  const catTotal = (cat.line_items || []).reduce((s, li) => s + (parseFloat(li.estimated_total) || 0), 0);
  return (
    <div style={{ marginBottom: 14, borderRadius: 8, overflow: 'hidden', border: '1px solid #bfdbfe' }}>
      <div style={{ background: '#1e3a8a', color: '#fff', padding: '6px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 800, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {index + 1}. {cat.category_name}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, opacity: 0.85 }}>
          {cat.line_items?.length ?? 0} line{cat.line_items?.length !== 1 ? 's' : ''}
          {catTotal > 0 ? ` · ₦${catTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
        </span>
      </div>
      <div style={{ overflowX: 'auto', background: '#fff' }}>
        <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#dbeafe' }}>
              {['#', 'Description / Materials', 'Req Qty', 'UOM', 'Est. Rate', 'Est. Total', 'Remarks', 'Materials'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: h === '#' || h === 'Materials' ? 'center' : 'left', fontWeight: 700, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#1e3a8a', whiteSpace: 'nowrap', borderBottom: '1.5px solid #93c5fd' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(cat.line_items || []).length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 14, textAlign: 'center', color: '#94a3b8', fontStyle: 'italic', fontSize: 12 }}>No line items.</td></tr>
            ) : (
              (cat.line_items || []).map((li, i) => (
                <LineDetailRow key={li.line_item_id} li={li} index={i} />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main details view ────────────────────────────────────────────────────────
export default function MRDetailsView({ row, onBack }) {
  const [mr, setMr] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      api.clearApiCache();
      const mrRes = await api.getMaterialRequest(row.request_id);
      const mrData = mrRes?.data || mrRes;
      setMr(mrData);

      const wfId = row.workflow_instance_id || mrData?.workflow_instance_id;
      if (wfId) {
        const trackRes = await api.getWorkflowTracking(wfId).catch(() => null);
        setTracking(trackRes?.data || trackRes || null);
      }
    } catch (e) {
      toast.show('Failed to load material request details', 'error');
    } finally {
      setLoading(false);
    }
  }, [row.request_id, row.workflow_instance_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <RNLLoader title="Loading Request Details…" subtitle="Fetching material request and approval history" />;
  if (!mr) return (
    <div style={{ padding: 20 }}>
      Failed to load details. <button onClick={onBack}>Go Back</button>
    </div>
  );

  const sc = STATUS_CFG[mr.status] || { label: mr.status, color: '#64748b' };

  const timelineNodes = tracking ? [
    ...(tracking.approval_history || []).map((h, i) => ({
      kind: 'history', key: `h-${h.step_id || i}`,
      order: h.step_order ?? i + 1, hist: h,
    })),
    ...(tracking.current_step ? [{
      kind: 'current', key: 'cur',
      order: tracking.current_step.step_order ?? Number.MAX_SAFE_INTEGER,
    }] : []),
  ].sort((a, b) => a.order - b.order) : [];

  const HIST_STYLE = {
    approved: { color: '#10b981', icon: <polyline points="20 6 9 17 4 12" /> },
    returned: { color: '#f97316', icon: (<><polyline points="9 14 4 9 9 4" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>) },
    rejected: { color: '#ef4444', icon: (<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>) },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <style>{`
        @keyframes hourglass-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .mr-hg-pending { animation: hourglass-spin 2s linear infinite; transform-origin: center; }
      `}</style>

      {/* ── Back + Title ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            padding: '6px 16px', background: '#e0f2fe', color: '#1e3a8a',
            border: '2px solid #93c5fd', borderRadius: 9999,
            cursor: 'pointer', fontWeight: 700, fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back
        </button>
        <h2 style={{ margin: 0, fontSize: 20 }}>
          Request Details: {mr.request_ref || 'DRAFT'}
        </h2>
        <span style={{
          background: sc.color + '18', color: sc.color,
          border: `1px solid ${sc.color}40`,
          padding: '4px 12px', borderRadius: 12,
          fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
        }}>
          {sc.label.toUpperCase()}
        </span>
      </div>

      {/* ── Section 1 — Request Header ── */}
      <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '14px 18px' }}>
        <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
          Section 1 — Request Header
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '14px 28px' }}>
          {[
            ['Reference',      mr.request_ref || '—'],
            ['Status',         sc.label],
            ['Priority',       (mr.priority || 'normal').charAt(0).toUpperCase() + (mr.priority || 'normal').slice(1)],
            ['Building',       mr.building || '—'],
            ['Revision',       mr.revision || 'R0'],
            ['Requested By',   row.requested_by_name || '—'],
            ['Requested Date', fmtDate(mr.requested_date)],
            ['Submitted',      fmtDate(mr.created_at)],
            ['Categories',     mr.categories?.length ?? row.category_count ?? 0],
            ['Notes',          mr.notes || '—'],
          ].map(([lbl, val]) => (
            <div key={lbl}>
              <div style={fieldLabel}>{lbl}</div>
              <div style={fieldValue}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section 2 — Categories & Line Items ── */}
      {mr.categories?.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '14px 18px' }}>
          <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
            Section 2 — Categories &amp; Line Items
          </div>
          {mr.categories.map((cat, ci) => (
            <CategoryDetailBlock key={cat.category_id} cat={cat} index={ci} />
          ))}
        </div>
      )}

      {/* ── Approval Progress & History ── */}
      <div style={{ background: '#fff', border: '1px solid black', borderRadius: 8, padding: '14px 18px' }}>
        <div style={{ fontWeight: 800, color: '#1e3a8a', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
          </svg>
          Approval Progress &amp; History
        </div>

        {!tracking ? (
          <div style={{ color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
            No workflow data yet — this request has not been submitted.
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-start', overflowX: 'auto', paddingBottom: 8 }}>
            {/* Submitter node */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 140 }}>
              <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: '1.5px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: '2px solid #10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
              </div>
              <div style={{ fontWeight: 600, fontSize: 12, marginTop: 6 }}>Submitted By</div>
              <div style={{ color: '#475569', fontSize: 11 }}>{tracking.requester_details?.full_name || row.requested_by_name || '—'}</div>
            </div>

            {timelineNodes.map((node) => {
              if (node.kind === 'history') {
                const h = node.hist;
                const hs = HIST_STYLE[h.status] || HIST_STYLE.approved;
                const outcomeLabel = h.status === 'returned' ? ' (Returned)' : h.status === 'rejected' ? ' (Rejected)' : '';
                return (
                  <React.Fragment key={node.key}>
                    <div style={{ flex: '1 1 60px', minWidth: 60, height: 2, background: hs.color, marginTop: 19 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 140, maxWidth: 180 }}>
                      <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px solid ${hs.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${hs.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: hs.color }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">{hs.icon}</svg>
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, fontSize: 12, marginTop: 6 }}>{h.step_name}{outcomeLabel}</div>
                      <div style={{ color: '#475569', fontSize: 11 }}>{h.approver_fullname}</div>
                      <div style={{ color: '#94a3b8', fontSize: 10 }}>{fmtDateTime(h.date_actioned)}</div>
                      {h.comments && <div style={{ marginTop: 3, fontStyle: 'italic', color: '#475569', fontSize: 10 }}>"{h.comments}"</div>}
                    </div>
                  </React.Fragment>
                );
              }

              const cur = tracking.current_step;
              const isOnHold = cur.is_on_hold;
              const stepColor = isOnHold ? '#f97316' : '#eab308';
              return (
                <React.Fragment key={node.key}>
                  <div style={{ flex: '1 1 60px', minWidth: 60, height: 2, background: '#10b981', marginTop: 19 }} />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 140, maxWidth: 180 }}>
                    <div style={{ zIndex: 1, width: 40, height: 40, borderRadius: '50%', border: `1.5px solid ${stepColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: `2px solid ${stepColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: stepColor }}>
                        {isOnHold ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg className="mr-hg-pending" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M5 22h14" /><path d="M5 2h14" />
                            <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                            <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 12, color: stepColor, marginTop: 6 }}>
                      {cur.step_name}
                      <div style={{ fontSize: 11 }}>{isOnHold ? '(On Hold)' : '(Pending)'}</div>
                    </div>
                    {cur.assigned_to?.full_name && (
                      <div style={{ color: '#475569', fontSize: 11 }}>{cur.assigned_to.full_name}</div>
                    )}
                  </div>
                </React.Fragment>
              );
            })}

            {tracking.next_step && (
              <>
                <div style={{ flex: '1 1 60px', minWidth: 60, height: 2, borderTop: '2px dashed #94a3b8', marginTop: 19 }} />
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', minWidth: 140, maxWidth: 180, opacity: 0.65 }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', border: '1.5px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 30, height: 30, borderRadius: '50%', background: '#fff', border: '2px dashed #94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#64748b', marginTop: 6 }}>{tracking.next_step.step_name} (Next)</div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
